/**
 * useRights — frontend gating against the same matrix the backend enforces.
 *
 * Each yacht returned by GET /api/yachts now ships with a `my_rights` array
 * (e.g. `["inventory:create", "pettycash:approve", ...]`). YachtContext keeps
 * the active yacht in memory; this hook exposes a fast `can(action)` predicate
 * and a `role` string so UI components can hide/disable disallowed affordances.
 *
 * Backend remains the source of truth — frontend gating is purely UX polish.
 */
import { useMemo } from "react";
import { useYacht } from "../context/YachtContext";

export function useRights() {
  const { activeYacht } = useYacht();

  return useMemo(() => {
    const rights = new Set(activeYacht?.my_rights || []);
    const role = activeYacht?.my_role || null;
    const can = (action) => rights.has(action);
    const canAny = (...actions) => actions.some((a) => rights.has(a));
    const blockedModules = new Set(activeYacht?.my_blocked_modules || []);
    const moduleAllowed = (key) => !blockedModules.has(key);
    return { role, rights, can, canAny, blockedModules, moduleAllowed };
  }, [activeYacht?.id, activeYacht?.my_rights, activeYacht?.my_role, activeYacht?.my_blocked_modules]);
}

/* Role labels — kept in sync with backend rights.ROLE_LABELS_HUMAN. */
export const ROLE_LABEL = {
  owner: "Owner",
  manager: "Manager",
  captain: "Captain",
  first_officer: "First Officer",
  engineer: "Engineer",
  accountant: "Accountant",
  deckhand: "Deckhand",
  stewardess: "Stewardess",
  chef: "Chef",
  hostess: "Hostess",
  cleaner: "Cleaner",
  detailer: "Detailer",
  delivery_crew: "Delivery Crew",
  night_watch: "Night Watch",
  maintenance_helper: "Maintenance Helper",
  provider: "Online Supplier",
  viewer: "Viewer (read-only)",
};
